import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@/common/styles/style";
import { styles } from "./style";
import BackButton from "@/atoms/backButton";
import { apiMachineType } from "@/common/type/type";
import { useLocation } from "react-router-dom";
import SearchBox from "@/molecules/searchBox";
import { MoreVert } from "@mui/icons-material";
import MenuPopover from "@/molecules/menuPopover";
import InputPopover from "@/molecules/inputPopover";
import SortIconButton from "@/atoms/sortIconButton";
import CSVDownloadButton from "@/atoms/CSVDownloadButton";
import ReportDownloadButton from "@/atoms/reportDownloadButton";
import CSVDownloadDialog from "@/organisms/CSVDownloadDialog";
import { sortCollectedData } from "@/helpers/sortUtil";
import { useLoading } from "@/hooks/useLoading";
import CircularProgress from "@/molecules/circularProgress";
import { CollectionType } from "./type";
import { useApiError } from "@/hooks/useApiError";
import ErrorMessage from "@/atoms/errorMessage";
import ReportDownloadDialog from "@/organisms/reportDownloadDialog";

export default function CollectionDataSet() {
  const location = useLocation();
  const { selectedMachine }: { selectedMachine: apiMachineType } =
    location.state || {};
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [popupType, setPopupType] = useState<"editUnit" | "setLimit" | null>(
    null,
  );
  const [limitType, setLimitType] = useState<"upper" | "lower" | null>(null);
  const [limitValue, setLimitValue] = useState<number | "">("");
  const [editUnitValue, setEditUnitValue] = useState<string>("");
  const [collectedData, setCollectedData] = useState<CollectionType[]>([]);
  const [filteredMachines, setFilteredMachines] = useState<CollectionType[]>(
    [],
  );
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    column: keyof CollectionType;
    direction: "asc" | "desc";
  }>({ column: "dataName", direction: "asc" });
  const { loading, startLoading, stopLoading } = useLoading();
  const { Routes, navigate } = useApiError();

  const handleSort = (column: keyof CollectionType) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig.column === column && prevConfig.direction === "asc"
          ? "desc"
          : "asc";
      return { column, direction: newDirection };
    });
  };

  // 収集データ設定画面表示API
  useEffect(() => {
    const checkServerConnection = async () => {
      startLoading();
      try {
        const response = await fetch(
          `/api/collectedData/${selectedMachine.id}`,
          {
            method: "GET",
            credentials: "include",
          },
        );

        const data = await response.json();
        if (response.ok) {
          console.log(data.message);
          const resultData = Array.isArray(data.result.data)
            ? data.result.data
            : [];
          setCollectedData(resultData);
          setFilteredMachines(resultData);
        } else if (response.status === 404) {
          setCollectedData([]);
          setFilteredMachines([]);
          alert(data.message);
          navigate(Routes.MACHINE_LIST);
        } else if (response.status === 401 || response.status === 403) {
          alert(data.message);
          navigate(Routes.LOGIN);
        } else {
          setError(data.message);
        }
      } catch (error) {
        console.error("サーバ接続エラー:", error);
      } finally {
        stopLoading();
      }
    };
    checkServerConnection();
  }, [selectedMachine]);

  useEffect(() => {
    if (collectedData) {
      const sortedData = sortCollectedData(collectedData, sortConfig);
      setFilteredMachines(sortedData);
    }
  }, [sortConfig, collectedData]);

  const handleSearch = (value?: string | number) => {
    if (!value) {
      setFilteredMachines(collectedData);
      return;
    }

    const searchValue = typeof value === "string" ? value.toLowerCase() : value;

    const filtered =
      (filteredMachines as CollectionType[]).filter((collected) => {
        if (collected) {
          if (typeof searchValue === "string") {
            return (
              collected?.dataName.toLowerCase().includes(searchValue) ||
              collected.createAt.toLowerCase().includes(searchValue) ||
              collected.unit.toLowerCase().includes(searchValue) ||
              collected.value.toString().includes(searchValue) ||
              collected.min?.toString().includes(searchValue) ||
              collected.max?.toString().includes(searchValue)
            );
          }
        }
        return false;
      }) || collectedData;

    setFilteredMachines(filtered);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    rowIndex: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(rowIndex);
    setPopupType(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setPopupType(null);
    setLimitType(null);
    setLimitValue("");
    setEditUnitValue("");
  };

  // 収集データ設定更新する関数
  const updateDataToServer = async (
    type: "upper" | "lower" | "unit",
    value?: number | string,
  ) => {
    const requestBody: {
      max?: number | null;
      min?: number | null;
      unit?: string | null;
    } = {
      max: undefined,
      min: undefined,
      unit: undefined,
    };

    if (
      type === "upper" &&
      (typeof value === "number" || value === undefined)
    ) {
      requestBody.max = value === undefined ? null : value;
    } else if (
      type === "lower" &&
      (typeof value === "number" || value === undefined)
    ) {
      requestBody.min = value === undefined ? null : value;
    } else if (
      type === "unit" &&
      (typeof value === "string" || value === undefined)
    ) {
      requestBody.unit = value === undefined ? null : value;
    }

    // 収集データ設定更新API
    try {
      const response = await fetch(
        `/api/collectedData/${selectedMachine.id}/${filteredMachines[selectedRow as number].dataName}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );

      const data = await response.json();
      if (response.ok) {
        console.log(data.message);
      } else if (response.status === 404) {
        alert(data.message);
        navigate(Routes.ERROR);
      } else if (response.status === 401 || response.status === 403) {
        alert(data.message);
        navigate(Routes.LOGIN);
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error("サーバ接続エラー:", error);
    }
  };

  // 閾値制限設定
  const handleSetLimit = (type: "upper" | "lower") => {
    setLimitType(type);
    setPopupType("setLimit");
  };

  const handleLimitSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedRow !== null && limitType && limitValue !== "") {
      const value = Number(limitValue);
      if (isNaN(value)) {
        console.error("Limit value must be a valid number");
        return;
      }

      if (
        filteredMachines &&
        selectedRow >= 0 &&
        selectedRow < filteredMachines.length
      ) {
        const updatedData = [...filteredMachines];
        if (limitType === "upper") {
          updatedData[selectedRow].max = value;
        } else if (limitType === "lower") {
          updatedData[selectedRow].min = value;
        }

        setFilteredMachines(updatedData);
        updateDataToServer(limitType, value);
        handleMenuClose();
      }
    }
  };

  // 閾値削除設定
  const handleClearLimit = (type: "upper" | "lower") => {
    if (selectedRow !== null && filteredMachines) {
      const updatedData = [...filteredMachines];

      if (type === "upper") updatedData[selectedRow].max = undefined;
      if (type === "lower") updatedData[selectedRow].min = undefined;

      setFilteredMachines(updatedData);
      updateDataToServer(type, undefined);
      handleMenuClose();
    }
  };

  const handleEditUnit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!filteredMachines || !filteredMachines) {
      console.error("Selected machine or collected data is missing.");
      return;
    }

    if (selectedRow !== null && editUnitValue !== "") {
      const updatedData = [...filteredMachines];

      if (selectedRow >= 0 && selectedRow < updatedData.length) {
        updatedData[selectedRow].unit = editUnitValue;
        updateDataToServer("unit", editUnitValue);
        handleMenuClose();
      } else {
        console.error("Invalid row selection.");
      }
    } else {
      console.error("Please select a valid row and enter a unit value.");
    }
  };

  // 一行のデータを削除
  const handleDelete = async () => {
    if (selectedRow !== null && filteredMachines) {
      try {
        const response = await fetch(
          `/api/collectedData/${selectedMachine.id}/${filteredMachines[selectedRow as number].dataName}`,
          {
            method: "DELETE",
          },
        );

        const connectData = await response.json();
        if (response.ok) {
          console.log(connectData.message);

          const updatedData: CollectionType[] = filteredMachines.filter(
            (_, i) => i !== selectedRow,
          );
          setFilteredMachines(updatedData);
          handleMenuClose();
        } else if (response.status === 404) {
          alert(connectData.message);
          navigate(Routes.ERROR);
        } else {
          console.error("削除失敗: ", await response.text());
          alert("データ削除に失敗しました。");
        }
      } catch (error) {
        console.error("サーバ接続エラー:", error);
      }
    }
  };

  const handleCSVButton = () => {
    setOpen(!open);
  };

  const handleReportButton = () => {
    setReportOpen(!reportOpen);
  };

  const header = [
    { title: "名前", key: "dataName" },
    { title: "タイムスタンプ", key: "createAt" },
    { title: "値", key: "value" },
    { title: "単位", key: "unit" },
    { title: "閾値下限", key: "min" },
    { title: "閾値上限", key: "max" },
    { title: "", key: "" },
  ];

  const paper = [
    { name: "単位の編集", func: () => setPopupType("editUnit") },
    { name: "閾値下限の編集", func: () => handleSetLimit("lower") },
    { name: "閾値上限の編集", func: () => handleSetLimit("upper") },
    { name: "閾値下限クリア", func: () => handleClearLimit("lower") },
    { name: "閾値上限クリア", func: () => handleClearLimit("upper") },
    { name: "収集データ削除", func: handleDelete },
  ];

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Stack direction="row">
          <BackButton />
          <Typography variant="h3" sx={styles.title}>
            機械: {selectedMachine.machineNumber}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <ReportDownloadButton handleClick={handleReportButton} />
          <CSVDownloadButton handleClick={handleCSVButton} />
        </Stack>
        <ReportDownloadDialog
          open={reportOpen}
          onClose={handleReportButton}
          machineId={selectedMachine.id}
        />
        <CSVDownloadDialog
          open={open}
          onClose={handleCSVButton}
          machine={selectedMachine}
          collectedData={collectedData || []}
        />
      </Box>
      <ErrorMessage error={error} />
      {loading ? (
        <CircularProgress />
      ) : (
        <Stack sx={styles.table}>
          <SearchBox onSearch={handleSearch} />
          <Table>
            <TableHead sx={styles.tableHead}>
              <TableRow>
                {header.map(({ title, key }, index) => (
                  <TableCell align="center" key={index}>
                    <Typography variant="h6">
                      {title}
                      {key !== "" ? (
                        <SortIconButton
                          onSort={() => handleSort(key as keyof CollectionType)}
                          isAscending={
                            sortConfig.column === key &&
                            sortConfig.direction === "asc"
                          }
                        />
                      ) : (
                        ""
                      )}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMachines.length > 0 ? (
                filteredMachines.map((datalist, index) => {
                  const body = [
                    datalist.dataName,
                    datalist.createAt,
                    datalist.value,
                    datalist.unit,
                    datalist.min,
                    datalist.max,
                  ];

                  return (
                    <TableRow key={index}>
                      {body.map((bodydata, i) => (
                        <TableCell align="center" key={i}>
                          <Typography variant="body1">{bodydata}</Typography>
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <IconButton onClick={(e) => handleMenuOpen(e, index)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow sx={styles.noData}>
                  <TableCell colSpan={header.length} align="center">
                    <Typography variant="body1">
                      データがありません。
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Stack>
      )}

      {/* Popover Area */}
      <MenuPopover
        anchorEl={anchorEl}
        data={paper}
        handleMenuClose={handleMenuClose}
      />
      {popupType && (
        <InputPopover
          open={Boolean(popupType)}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          editValue={popupType === "setLimit" ? limitValue : editUnitValue}
          setEditValue={
            popupType === "setLimit"
              ? (value: string | number) => setLimitValue(Number(value))
              : (value: string | number) => setEditUnitValue(value as string)
          }
          handleEditSubmit={
            popupType === "setLimit" ? handleLimitSubmit : handleEditUnit
          }
          buttonLabel="編集"
          textProps={{
            label:
              popupType === "setLimit"
                ? limitType === "upper"
                  ? "閾値上限"
                  : "閾値下限"
                : "Unit",
            fullWidth: true,
          }}
        />
      )}
    </Box>
  );
}
